(function () {
  const nucerySearchInstances = [];

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function debounce(fn, delay) {
    let timer;
    const debounced = function debounced() {
      const args = arguments;
      const ctx = this;
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(ctx, args), delay);
    };
    debounced.cancel = function cancel() {
      clearTimeout(timer);
      timer = null;
    };
    return debounced;
  }

  function featuredImageUrl(product) {
    if (!product) return '';
    if (product.featured_image) {
      const fi = product.featured_image;
      if (typeof fi === 'string') return fi;
      return fi.url || '';
    }
    return product.image || '';
  }

  class NuceryHeaderSearch {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('.nucery-search__panel');
      this.headerSection =
        this.root.closest('.section-impulse-header') || this.root.closest('.shopify-section');
      const headerScope = this.headerSection || document;
      this.desktopInput = headerScope.querySelector('[data-nucery-search-input-desktop]');
      this.panelInput = root.querySelector('[data-nucery-search-input]');
      this.desktopForm = headerScope.querySelector('[data-nucery-search-desktop-form]');
      this.desktopSearchWrap = headerScope.querySelector('[data-nucery-desktop-search-wrap]');
      this.desktopExpandTrigger = headerScope.querySelector('[data-nucery-desktop-search-trigger]');
      this.desktopSubmit = this.desktopForm ? this.desktopForm.querySelector('button[type="submit"]') : null;
      this.panelForm = root.querySelector('[data-nucery-search-form]');
      this.openButtons = Array.from(headerScope.querySelectorAll('[data-nucery-search-open]'));
      this.suppressDesktopBlur = false;
      this.closeButtons = Array.from(root.querySelectorAll('[data-nucery-search-close]'));
      this.chips = Array.from(root.querySelectorAll('[data-nucery-search-chip]'));
      this.productsContainer =
        root.querySelector('[data-nucery-search-products-dynamic]') ||
        root.querySelector('[data-nucery-search-products]');
      this.staticWrap = root.querySelector('[data-nucery-search-static]');
      this.dynamicWrap = root.querySelector('[data-nucery-search-dynamic]');
      this.emptyEl = root.querySelector('[data-nucery-search-empty]');
      this.countEl = root.querySelector('[data-nucery-search-results-count]');
      this.viewAllWrap = root.querySelector('[data-nucery-search-view-all-wrap]');
      this.viewAllLink = root.querySelector('[data-nucery-search-view-all]');
      this.viewAllLabel = root.dataset.viewAllLabel || 'View All';
      this.resultsPrefix = root.dataset.resultsPrefix || 'RESULTS';
      this.noResultsText = root.dataset.noResultsText || 'No results found.';
      this.searchMode = 'desktop';
      this.isOpen = false;
      this.lastQuery = '';
      this.fetchId = 0;
      this.prevBodyOverflow = '';
      this.prevHtmlOverflow = '';
      this.prevBodyPosition = '';
      this.prevBodyTop = '';
      this.prevBodyLeft = '';
      this.prevBodyRight = '';
      this.prevBodyWidth = '';
      this.scrollY = 0;
      this.scrollLockActive = false;

      const ds = root.dataset;
      this.cardConfig = {
        showQuickAdd: ds.searchCardShowQuickAdd !== 'false',
        showReviews: ds.searchCardShowReviews !== 'false',
        showSaleBadge: ds.searchCardShowSaleBadge !== 'false',
        saleBadgeText: ds.searchCardSaleBadgeText || 'Sale',
        strikeCompare: ds.searchCardStrikeCompare !== 'false',
        showBestsellerBadge: ds.searchCardShowBestsellerBadge !== 'false',
        bestsellerTag: (ds.searchCardBestsellerTag || '').toLowerCase().trim(),
        bestsellerBadgeText: ds.searchCardBestsellerBadgeText || 'Best seller',
        sectionId: ds.searchSectionId || 'search',
      };

      this.requestResults = debounce((query) => this.fetchResults(query), 220);
      nucerySearchInstances.push(this);
      this.bindEvents();
      this.updatePanelOffset();
      this.renderPopular();
    }

    focusSearchInput(mode) {
      const isDesktop =
        mode === 'desktop' ||
        (mode !== 'mobile' && window.matchMedia('(min-width: 990px)').matches);
      const input = isDesktop ? this.desktopInput : this.panelInput;
      if (!input) return;

      if (isDesktop) {
        this.suppressDesktopBlur = true;
      }

      const tryFocus = () => {
        if (!input.isConnected) return document.activeElement === input;
        try {
          input.focus({ preventScroll: true });
        } catch (err) {
          input.focus();
        }
        return document.activeElement === input;
      };

      const finishFocus = () => {
        tryFocus();
        if (isDesktop) {
          window.setTimeout(() => {
            this.suppressDesktopBlur = false;
          }, 300);
        }
      };

      if (isDesktop) {
        this.expandDesktopSearch(true);
        finishFocus();
        requestAnimationFrame(finishFocus);
        window.setTimeout(finishFocus, 50);
        window.setTimeout(finishFocus, 120);
        return;
      }

      tryFocus();
      requestAnimationFrame(finishFocus);
      window.setTimeout(finishFocus, 80);
    }

    expandDesktopSearch(instant) {
      if (!this.desktopSearchWrap) return;
      this.desktopSearchWrap.classList.add('is-expanded');
      if (instant) {
        this.desktopSearchWrap.classList.add('is-expanded--instant');
        requestAnimationFrame(() => {
          if (this.desktopSearchWrap) {
            this.desktopSearchWrap.classList.remove('is-expanded--instant');
          }
        });
      }
      if (this.desktopExpandTrigger) {
        this.desktopExpandTrigger.setAttribute('aria-expanded', 'true');
      }
    }

    onHeaderSearchIconClick() {
      if (!this.desktopSearchWrap || !this.desktopInput) return;
      this.expandDesktopSearch(true);
      this.open('desktop');
      this.focusSearchInput('desktop');
    }

    bindEvents() {
      this.openButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const isDesktop = window.matchMedia('(min-width: 990px)').matches;
          const mode = isDesktop ? 'desktop' : 'mobile';
          if (isDesktop) {
            this.onHeaderSearchIconClick();
          } else {
            this.open(mode);
            this.focusSearchInput('mobile');
          }
        });
      });

      this.closeButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });

      if (this.desktopInput) {
        this.desktopInput.addEventListener('focus', () => {
          this.open('desktop');
          if (this.desktopSearchWrap) this.desktopSearchWrap.classList.add('is-expanded');
          if (this.desktopExpandTrigger) this.desktopExpandTrigger.setAttribute('aria-expanded', 'true');
        });
        this.desktopInput.addEventListener('click', () => this.open('desktop'));
        this.desktopInput.addEventListener('input', (e) => this.handleQueryChange(e.target.value, 'desktop'));
        this.desktopInput.addEventListener('search', (e) => {
          if (!(e.target.value || '').trim()) {
            this.handleQueryChange('', 'desktop');
          }
        });
        this.desktopInput.addEventListener('blur', () => {
          window.setTimeout(() => {
            if (this.suppressDesktopBlur) return;
            if (!this.desktopSearchWrap || !document.activeElement) return;
            if (this.isOpen && this.searchMode === 'desktop') return;
            if (!this.desktopSearchWrap.contains(document.activeElement)) {
              this.desktopSearchWrap.classList.remove('is-expanded');
              if (this.desktopExpandTrigger) this.desktopExpandTrigger.setAttribute('aria-expanded', 'false');
            }
          }, 120);
        });
      }

      if (this.desktopExpandTrigger && this.desktopSearchWrap) {
        this.desktopExpandTrigger.addEventListener('mousedown', (e) => {
          if (e.button === 0) e.preventDefault();
        });
        this.desktopExpandTrigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.onHeaderSearchIconClick();
        });
      }

      if (this.desktopSubmit) {
        this.desktopSubmit.addEventListener('mousedown', (e) => {
          if (e.button === 0) e.preventDefault();
        });
        this.desktopSubmit.addEventListener('click', (event) => {
          const q = (this.desktopInput && this.desktopInput.value ? this.desktopInput.value : '').trim();
          if (!q) {
            event.preventDefault();
            if (this.desktopInput) this.focusSearchInput('desktop');
          }
        });
      }

      if (this.panelInput) {
        this.panelInput.addEventListener('focus', () => this.open(window.matchMedia('(min-width: 990px)').matches ? 'desktop' : 'mobile'));
        this.panelInput.addEventListener('input', (e) => this.handleQueryChange(e.target.value, 'panel'));
        this.panelInput.addEventListener('search', (e) => {
          if (!(e.target.value || '').trim()) {
            this.handleQueryChange('', 'panel');
          }
        });
      }

      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => {
          const term = chip.getAttribute('data-nucery-search-chip') || '';
          this.handleQueryChange(term, 'chip');
          if (this.panelInput) {
            this.focusSearchInput('mobile');
          } else if (this.desktopInput) {
            this.expandDesktopSearch();
            this.focusSearchInput('desktop');
          }
        });
      });

      if (this.desktopForm) {
        this.desktopForm.addEventListener('submit', (event) => {
          // Never navigate to /search on Enter — keep the predictive results
          // panel open instead. (Use "View all" for the full results page.)
          event.preventDefault();
          const q = (this.desktopInput && this.desktopInput.value ? this.desktopInput.value : '').trim();
          if (!q) {
            this.focusSearchInput('desktop');
            return;
          }
          this.open('desktop');
          this.handleQueryChange(q, 'desktop');
        });
      }
      if (this.panelForm) {
        this.panelForm.addEventListener('submit', (event) => {
          // Never navigate to /search on Enter — keep the results visible.
          event.preventDefault();
          const q = (this.panelInput && this.panelInput.value ? this.panelInput.value : '').trim();
          if (q) this.handleQueryChange(q, 'panel');
        });
      }

      document.addEventListener('click', (event) => {
        if (!this.isOpen) return;
        const target = event.target;
        const clickedOpenBtn = this.openButtons.some((btn) => btn.contains(target));
        const clickedDesktopForm = this.desktopForm && this.desktopForm.contains(target);
        const clickedDesktopWrap = this.desktopSearchWrap && this.desktopSearchWrap.contains(target);
        const clickedPanel = this.panel && this.panel.contains(target);
        // The shared quick-add popup is rendered on document.body (outside the
        // search panel). Interacting with it (open from a result card, pick a
        // variant, close) must NOT close the search behind it.
        const quickAdd = document.getElementById('NuceryQuickAdd');
        const clickedQuickAdd = quickAdd && quickAdd.contains(target);
        if (!clickedOpenBtn && !clickedDesktopForm && !clickedDesktopWrap && !clickedPanel && !clickedQuickAdd) {
          this.close();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.isOpen) return;
        // If the quick-add popup is open over the search, let Escape close that
        // first and leave the search open.
        const quickAdd = document.getElementById('NuceryQuickAdd');
        if (quickAdd && quickAdd.classList.contains('is-open')) return;
        this.close();
      });

      window.addEventListener('resize', () => {
        this.updatePanelOffset();
        if (!window.matchMedia('(min-width: 990px)').matches) {
          if (this.desktopSearchWrap) this.desktopSearchWrap.classList.remove('is-expanded');
          if (this.desktopExpandTrigger) this.desktopExpandTrigger.setAttribute('aria-expanded', 'false');
        }
        if (!this.isOpen) return;
        if (window.matchMedia('(min-width: 990px)').matches && this.searchMode !== 'desktop') {
          this.open('desktop');
        }
      });

      window.addEventListener(
        'scroll',
        () => {
          if (this.isOpen) this.updatePanelOffset();
        },
        { passive: true }
      );
    }

    getHeaderHeight() {
      const header = document.querySelector('#HeaderWrapper');
      if (!header) return 60;
      const rect = header.getBoundingClientRect();
      const measured = Math.round(rect.height || header.offsetHeight || 0);
      if (measured > 0) return measured;
      const cssVar = parseFloat(
        getComputedStyle(header).getPropertyValue('--nucery-header-bar-height')
      );
      return Number.isFinite(cssVar) && cssVar > 0 ? Math.round(cssVar) : 60;
    }

    updatePanelOffset() {
      const section = this.headerSection;
      if (!section) return;
      const headerHeight = this.getHeaderHeight();
      if (document.documentElement.classList.contains('nucery-search-open')) {
        section.style.setProperty('--nucery-search-top', `${headerHeight}px`);
        return;
      }
      const header = document.querySelector('#HeaderWrapper');
      if (!header) return;
      const rect = header.getBoundingClientRect();
      const top = Math.max(headerHeight, Math.round(rect.bottom));
      section.style.setProperty('--nucery-search-top', `${top}px`);
    }

    lockScroll() {
      if (this.scrollLockActive) return;
      this.scrollY = window.scrollY || window.pageYOffset || 0;
      this.prevBodyOverflow = document.body.style.overflow || '';
      this.prevHtmlOverflow = document.documentElement.style.overflow || '';
      this.prevBodyPosition = document.body.style.position || '';
      this.prevBodyTop = document.body.style.top || '';
      this.prevBodyLeft = document.body.style.left || '';
      this.prevBodyRight = document.body.style.right || '';
      this.prevBodyWidth = document.body.style.width || '';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      this.scrollLockActive = true;
    }

    unlockScroll() {
      if (!this.scrollLockActive) return;
      document.body.style.position = this.prevBodyPosition;
      document.body.style.top = this.prevBodyTop;
      document.body.style.left = this.prevBodyLeft;
      document.body.style.right = this.prevBodyRight;
      document.body.style.width = this.prevBodyWidth;
      document.documentElement.style.overflow = this.prevHtmlOverflow;
      document.body.style.overflow = this.prevBodyOverflow;
      window.scrollTo(0, this.scrollY);
      this.scrollLockActive = false;
    }

    getActiveQuery() {
      if (this.searchMode === 'mobile' && this.panelInput) {
        return (this.panelInput.value || '').trim();
      }
      if (this.searchMode === 'desktop' && this.desktopInput) {
        return (this.desktopInput.value || '').trim();
      }
      const panelVal = this.panelInput ? this.panelInput.value : '';
      const desktopVal = this.desktopInput ? this.desktopInput.value : '';
      return (panelVal || desktopVal || '').trim();
    }

    cancelPendingSearch() {
      if (this.requestResults && typeof this.requestResults.cancel === 'function') {
        this.requestResults.cancel();
      }
      this.fetchId += 1;
    }

    isQueryStillCurrent(query) {
      return this.getActiveQuery() === (query || '').trim();
    }

    syncFromInputs() {
      const query = this.getActiveQuery();
      this.lastQuery = query;
      if (!query) {
        this.renderPopular();
        return;
      }
      this.requestResults(query);
    }

    open(mode) {
      document.documentElement.classList.add('nucery-search-open');
      this.searchMode = mode === 'desktop' ? 'desktop' : 'mobile';
      if (this.searchMode === 'mobile') {
        this.lockScroll();
      }
      this.updatePanelOffset();
      this.root.classList.add('is-open');
      this.root.classList.toggle('is-desktop', this.searchMode === 'desktop');
      this.isOpen = true;
      this.syncFromInputs();
    }

    close() {
      if (this.panelInput) this.panelInput.value = '';
      if (this.desktopInput) this.desktopInput.value = '';
      this.lastQuery = '';
      this.renderPopular();
      this.root.classList.remove('is-open', 'is-desktop');
      if (this.scrollLockActive) {
        document.body.style.overflow = this.prevBodyOverflow;
        document.documentElement.style.overflow = this.prevHtmlOverflow;
        this.scrollLockActive = false;
      }
      this.isOpen = false;
      if (this.desktopSearchWrap) this.desktopSearchWrap.classList.remove('is-expanded');
      if (this.desktopExpandTrigger) this.desktopExpandTrigger.setAttribute('aria-expanded', 'false');
    }

    handleQueryChange(query, source) {
      const value = (query || '').trimStart();
      this.lastQuery = value;

      if (source !== 'desktop' && this.desktopInput && this.desktopInput.value !== value) {
        this.desktopInput.value = value;
      }
      if (source !== 'panel' && this.panelInput && this.panelInput.value !== value) {
        this.panelInput.value = value;
      }

      if (!this.isOpen) this.open(window.matchMedia('(min-width: 990px)').matches ? 'desktop' : 'mobile');

      const trimmed = value.trim();
      if (!trimmed) {
        this.renderPopular();
        return;
      }

      if (this.requestResults && typeof this.requestResults.cancel === 'function') {
        this.requestResults.cancel();
      }
      this.requestResults(trimmed);
    }

    formatMoney(cents) {
      if (cents == null || cents === '') return '';
      if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === 'function') {
        const format = (window.theme.moneyFormat || (window.Shopify && window.Shopify.money_format) || '${{amount}}');
        return window.theme.Currency.formatMoney(cents, format);
      }
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents) / 100);
    }

    isBundleHidden(product) {
      if (!product) return false;
      // Shopify's search/suggest.json does NOT return product tags, so the
      // `bundle-hidden` tag can never match here. Hide the bundle-builder
      // pieces/anchors by their $0 price instead (that IS returned by suggest.json).
      const priceNum = Number(product.price);
      if (!Number.isNaN(priceNum) && priceNum <= 0) return true;
      const tags = product.tags || [];
      return tags.some((t) => String(t).toLowerCase() === 'bundle-hidden');
    }

    productHasBestsellerTag(product) {
      const tag = this.cardConfig.bestsellerTag;
      if (!tag || !product || !product.tags || !product.tags.length) return false;
      return product.tags.some((t) => String(t).toLowerCase() === tag);
    }

    buildSuggestProductCard(product, cartRoot) {
      const c = this.cardConfig;
      const title = escapeHtml(product.title || '');
      const url = escapeHtml(product.url || '#');
      const imgRaw = featuredImageUrl(product);
      const image = imgRaw ? escapeHtml(imgRaw) : '';

      const priceNum = Number(product.price);
      const compareRaw = product.compare_at_price;
      const compareNum = compareRaw != null && compareRaw !== '' ? Number(compareRaw) : NaN;
      const isSale = !Number.isNaN(compareNum) && !Number.isNaN(priceNum) && compareNum > priceNum;

      const price = this.formatMoney(product.price);
      const comparePriceStr = !Number.isNaN(compareNum) ? this.formatMoney(compareNum) : '';

      let badges = '';
      if (c.showBestsellerBadge && this.productHasBestsellerTag(product)) {
        badges += `<span class="bestsellers-card__badge bestsellers-card__badge--left">${escapeHtml(c.bestsellerBadgeText)}</span>`;
      }
      if (c.showSaleBadge && isSale) {
        badges += `<span class="bestsellers-card__badge bestsellers-card__badge--right">${escapeHtml(c.saleBadgeText)}</span>`;
      }

      let quick = '';
      if (c.showQuickAdd) {
        // Route the bag through the shared quick-add module (data-nqa-trigger +
        // data-nqa-decide). suggest.json does NOT return the full variant list,
        // so the count here is unreliable — the module fetches the real product
        // and decides: single buyable variant → add straight to cart; multiple
        // options → open the variant picker. Falls back to the product link only
        // when no handle can be resolved.
        const qhandle = product.handle || ((String(url).match(/\/products\/([^/?#]+)/) || [])[1] || '');
        const variants = product.variants || [];
        if (qhandle) {
          quick = `<div class="bestsellers-card__quick"><button type="button" class="bestsellers-card__quick-btn" data-nqa-trigger data-nqa-decide data-product-handle="${escapeHtml(qhandle)}" data-variant-count="${variants.length || 1}" aria-label="Add to cart: ${title}"><svg class="bestsellers-card__bag" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg></button></div>`;
        } else {
          quick = `<div class="bestsellers-card__quick"><a class="bestsellers-card__quick-btn bestsellers-card__quick-btn--go" href="${url}"><svg class="bestsellers-card__bag" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg></a></div>`;
        }
      }

      const strike =
        c.strikeCompare && isSale && comparePriceStr
          ? `<s class="bestsellers-card__price-old">${escapeHtml(comparePriceStr)}</s>`
          : '';
      const priceCls = `bestsellers-card__price-current${isSale ? ' is-sale' : ''}`;

      return `<li class="bestsellers-card"><div class="bestsellers-card__media" data-bst-carousel data-images="1" data-enable-slider="false"><div class="bestsellers-card__viewport"><div class="bestsellers-card__slides" data-bst-slides style="--bst-n: 1; --bst-i: 0;"><div class="bestsellers-card__slide" data-bst-slide">${image ? `<img class="bestsellers-card__img" src="${image}" alt="${title}" loading="lazy" width="800" height="800">` : ''}</div></div><a class="bestsellers-card__stretched-link" href="${url}" aria-label="${title} — view product"></a><div class="bestsellers-card__overlays">${badges}${quick}</div></div></div><div class="bestsellers-card__info"><a class="bestsellers-card__title" href="${url}">${title}</a><div class="bestsellers-card__price">${strike}<span class="${priceCls}">${escapeHtml(price)}</span></div></div></li>`;
    }

    renderCards(products) {
      if (!this.productsContainer) return;
      const cartRoot = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/?$/, '/');
      this.productsContainer.innerHTML = products.map((product) => this.buildSuggestProductCard(product, cartRoot)).join('');
      if (typeof window.initBestsellerCarousels === 'function') {
        window.initBestsellerCarousels(this.productsContainer);
      }
    }

    setViewAllVisible(visible) {
      if (!this.viewAllWrap) return;
      if (visible) {
        this.viewAllWrap.removeAttribute('hidden');
        this.viewAllWrap.classList.remove('nucery-search__view-all-wrap--hidden');
        this.viewAllWrap.style.display = '';
        this.viewAllWrap.setAttribute('aria-hidden', 'false');
      } else {
        this.viewAllWrap.setAttribute('hidden', '');
        this.viewAllWrap.classList.add('nucery-search__view-all-wrap--hidden');
        this.viewAllWrap.style.display = 'none';
        this.viewAllWrap.setAttribute('aria-hidden', 'true');
      }
    }

    setSearchMode(mode) {
      // 'popular' = before typing (static only)
      // 'results' = matches found (dynamic grid only)
      // 'empty'   = typed but no matches (error line + popular/trending below)
      const showPopular = mode === 'popular';
      const isEmpty = mode === 'empty';
      const showStatic = showPopular || isEmpty;
      const showDynamic = !showPopular; // results + empty
      this.root.classList.toggle('is-search-popular', showPopular);
      this.root.classList.toggle('is-search-results', showDynamic);
      this.root.classList.toggle('is-search-empty', isEmpty);
      if (this.staticWrap) {
        this.staticWrap.hidden = !showStatic;
        this.staticWrap.setAttribute('aria-hidden', showStatic ? 'false' : 'true');
      }
      if (this.dynamicWrap) {
        this.dynamicWrap.hidden = !showDynamic;
        this.dynamicWrap.setAttribute('aria-hidden', showDynamic ? 'false' : 'true');
      }
    }

    renderPopular() {
      this.cancelPendingSearch();
      this.setSearchMode('popular');
      if (this.emptyEl) this.emptyEl.hidden = true;
      if (this.productsContainer) this.productsContainer.innerHTML = '';
      if (this.countEl) this.countEl.textContent = `${this.resultsPrefix} (0)`;
      this.setViewAllVisible(false);
    }

    renderNoResults(query) {
      if (!this.isQueryStillCurrent(query)) {
        return;
      }
      // Show the error line AND keep popular items / trending visible below it.
      this.setSearchMode('empty');
      if (this.productsContainer) this.productsContainer.innerHTML = '';
      if (this.emptyEl) {
        this.emptyEl.hidden = false;
        this.emptyEl.textContent = `${this.noResultsText} ("${query}")`;
      }
      if (this.countEl) this.countEl.textContent = `${this.resultsPrefix} (0)`;
      this.setViewAllVisible(false);
    }

    renderResults(products, query) {
      if (!this.isQueryStillCurrent(query)) {
        return;
      }
      this.setSearchMode('results');
      if (this.emptyEl) this.emptyEl.hidden = true;
      if (this.countEl) this.countEl.textContent = `${this.resultsPrefix} (${products.length})`;
      if (this.viewAllLink) {
        this.viewAllLink.textContent = this.viewAllLabel;
        this.viewAllLink.setAttribute('href', `/search?q=${encodeURIComponent(query)}&type=product`);
      }
      this.setViewAllVisible(products.length > 0);
      this.renderCards(products);
    }

    fetchResults(query) {
      const trimmed = (query || '').trim();
      if (!trimmed) {
        this.renderPopular();
        return;
      }
      const activeRequestId = ++this.fetchId;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const params = new URLSearchParams();
      params.set('q', trimmed);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '12');
      params.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
      params.set('resources[options][unavailable_products]', 'last');

      fetch(`${root}search/suggest.json?${params.toString()}`, {
        headers: { Accept: 'application/json' }
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(res.statusText)))
        .then((json) => {
          if (activeRequestId !== this.fetchId) return;
          if (!this.isQueryStillCurrent(trimmed)) return;
          let products = (((json || {}).resources || {}).results || {}).products || [];
          // Hide bundle-only ($0) pieces tagged `bundle-hidden` from search results.
          products = products.filter((p) => !this.isBundleHidden(p));
          if (!products.length) {
            this.renderNoResults(trimmed);
            return;
          }
          this.renderResults(products, trimmed);
        })
        .catch(() => {
          if (activeRequestId !== this.fetchId) return;
          if (!this.isQueryStillCurrent(trimmed)) return;
          this.renderNoResults(trimmed);
        });
    }
  }

  let drawerCloseSearchListenerBound = false;

  function bindCloseSearchOnDrawerOpen() {
    if (drawerCloseSearchListenerBound) return;
    drawerCloseSearchListenerBound = true;
    document.addEventListener('drawerOpen', () => {
      nucerySearchInstances.forEach((inst) => {
        if (inst && typeof inst.close === 'function') inst.close();
      });
    });
  }

  function init() {
    bindCloseSearchOnDrawerOpen();
    document.querySelectorAll('[data-nucery-search]').forEach((root) => {
      if (!root.__nucerySearchBound) {
        root.__nucerySearchBound = true;
        new NuceryHeaderSearch(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
